/**
 * designSystem.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for ALL shared sx / style constants used across the
 * app (headers, buttons, search inputs, table toolbars, tabs, icon buttons).
 *
 * Import ONLY what you need — tree-shaking will drop the rest.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { bgcolor, border, borderBottom, textTransform } from "@mui/system";

// ── Brand palette (mirrors tokens.js for non-theme contexts) ─────────────────
export const COLOR = {
  primary: 'rgba(74, 102, 241, 1)',
  primary90: 'rgba(74, 102, 241, 0.9)',
  primary50: 'rgba(74, 102, 241, 0.5)',
  primary30: 'rgba(74, 102, 241, 0.3)',
  primary16: 'rgba(74, 102, 241, 0.16)',
  primary08: 'rgba(74, 102, 241, 0.08)',
  primary04: 'rgba(74, 102, 241, 0.04)',
  textDark: '#1a1a1a',
  textPrimary: '#212121',
  textSecondary: '#616161',
  textMedium: '#424242',
  border: '#E0E0E0',
  borderLight: '#EBEBEB',
  bg: '#ffffff',
  bgLight: '#FAFAFA',
  bgTableHeader: '#F5F5F5',
  shadowPrimary: '0px 4px 12px rgba(74, 102, 241, 0.3)',
  shadowPrimaryHover: '0px 6px 16px rgba(74, 102, 241, 0.4)',
  shadowSm: '0px 1px 3px rgba(0, 0, 0, 0.08)',
  shadowMd: '0px 2px 8px rgba(0, 0, 0, 0.08)',
};

// ── Page header container ─────────────────────────────────────────────────────
/** Outer wrapper for every page header row */
export const headerContainerSx = {
  // padding: '14px 20px',
  minHeight: 56,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 2,
  // bgcolor: '#FFFFFF',
  // borderBottom: "1px solid rgba(199, 199, 199, 0.67)",
  // p: 1
};

/** Typography style for the page/section title */
export const headerTitleSx = {
};

/** Right-side action cluster in the header */
export const headerActionsSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
};

// ── Search input ──────────────────────────────────────────────────────────────
/** Outer TextField sx used by ALL search inputs in the app */
export const searchFieldSx = {
  bgcolor: COLOR.bgLight,
  width: { xs: '220px', sm: '280px' },

  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.8125rem',
    height: 38,
    pr: '6px',
    bgcolor: COLOR.bgLight,

    '& fieldset': { borderColor: '#E8E8E8' },
    '&:hover fieldset': { borderColor: COLOR.primary50 },
    '&.Mui-focused fieldset': { borderColor: COLOR.primary },
  },

  '& .MuiInputBase-input': {
    py: '8px',
    fontSize: '0.8125rem',
  },
};

/** Inline search icon color */
export const searchIconSx = { color: '#AEAEAE', fontSize: 15 };

/** Inline "Search" button inside the input adornment */
export const searchButtonSx = {
  minWidth: 'unset',
  height: 26,
  px: 1.8,
  borderRadius: '6px',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  backgroundColor: COLOR.primary,
  color: '#fff',
  '&:hover': {
    backgroundColor: COLOR.primary90,
    boxShadow: 'none',
  },
};

// ── Primary action button (New Order / Create Customer / etc.) ────────────────
export const primaryButtonSx = {
  height: 38,
  px: 2.5,
  borderRadius: '8px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  boxShadow: COLOR.shadowPrimary,
  backgroundColor: COLOR.primary,
  color: '#fff',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: COLOR.primary90,
    boxShadow: COLOR.shadowPrimaryHover,
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: COLOR.primary50,
    color: '#fff',
    boxShadow: 'none',
  },
};

// ── Secondary (outlined) button ───────────────────────────────────────────────
export const secondaryButtonSx = {
  height: 38,
  px: 2.5,
  borderRadius: '8px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  borderColor: COLOR.border,
  color: COLOR.textSecondary,
  backgroundColor: 'transparent',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: COLOR.primary50,
    color: COLOR.primary,
    backgroundColor: COLOR.primary08,
  },
};

// ── Export / download button (a subdued contained variant) ────────────────────
export const exportButtonSx = {
  height: 38,
  px: 2,
  borderRadius: '8px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  backgroundColor: COLOR.primary,
  color: '#fff',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: COLOR.primary90,
    boxShadow: 'none',
  },
  '&:disabled': {
    opacity: 0.7,
    color: '#fff',
  },
  // success state — override via sx prop when needed
};

// ── Icon button (View / Impersonate / etc.) ───────────────────────────────────
export const iconButtonSx = {
  color: COLOR.textSecondary,
  borderRadius: '6px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: COLOR.primary08,
    color: COLOR.primary,
  },
};

// ── Tabs ──────────────────────────────────────────────────────────────────────
export const tabsSx = {
  '& .MuiTabs-indicator': {
    height: 3,
    backgroundColor: COLOR.primary,
    borderRadius: '3px 3px 0 0',
  },
  '& .MuiTab-root': {
    minHeight: 48,
    fontSize: '14px',
    fontWeight: 500,
    textTransform: "capitalize",
    // padding: '12px 24px',
    color: COLOR.textSecondary,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      color: COLOR.primary90,
      backgroundColor: COLOR.primary04,
    },
    '&.Mui-selected': {
      color: COLOR.primary,
      fontWeight: 600,
    },
  },
};

// ── Table cell base ───────────────────────────────────────────────────────────
export const tableCellSx = {
  borderBottom: `1px solid ${COLOR.borderLight}`,
  fontSize: '14px',
  color: COLOR.textPrimary,
};

/** Sticky header cell (inside <TableHead>) */
export const tableHeaderCellSx = {
  backgroundColor: `${COLOR.bgLight} !important`,
  color: COLOR.textSecondary,
  fontWeight: 700,
  fontSize: '13px',
  whiteSpace: 'nowrap',
  borderBottom: `1px solid ${COLOR.borderLight}`,
};

// ── Table toolbar (sort row above column headers) ─────────────────────────────
export const tableToolbarCellSx = {
  borderTop: `1px solid ${COLOR.border}`,
  borderBottom: `1px solid ${COLOR.border}`,
};

// ── Bulk-action bar (shown when rows are selected) ────────────────────────────
export const bulkActionBarSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  justifyContent: 'flex-end',
  mt: 2,
  mb: 2,
  px: 2,
};

// ── MUI Dialog shared styles ──────────────────────────────────────────────────
export const dialogPaperSx = {
  borderRadius: 3,
  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
  border: `1px solid ${COLOR.border}`,
};

export const dialogBackdropSx = {
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
  },
};

export const dialogTitleSx = {
  fontWeight: 700,
  fontSize: '1.125rem',
  color: COLOR.textDark,
  pb: 1,
  pt: 3,
  px: 3.5,
};

export const dialogContentSx = { px: 3.5, py: 2 };

export const dialogActionsSx = {
  px: 3.5,
  pb: 3,
  pt: 2,
  gap: 1.5,
};

// ── Chip helpers ──────────────────────────────────────────────────────────────
export const statusChipSx = {
  // fontWeight: 600,
  fontSize: '0.6875rem',
  height: 22,
  '& .MuiChip-label': { px: 1, py: 0 },
};
